import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Loader2, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ExportTemplate } from '../api/exportTemplateApi';
import type { JobExportField } from '../api/jobListApi';

interface ExportFieldsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Available columns, from the server's export field registry. */
  fields: JobExportField[];
  isLoading?: boolean;
  isExporting?: boolean;
  /** Called with the selected column keys, in registry order. */
  onConfirm: (selectedKeys: string[]) => void;
  /** Saved export templates visible to the user (own + shared). */
  templates?: ExportTemplate[];
  /** Persist the current selection as a new template. */
  onSaveTemplate?: (name: string, fields: string[], isShared: boolean) => Promise<void>;
  onDeleteTemplate?: (id: number) => Promise<void>;
  /** Id of the current user, so only their own templates offer a delete action. */
  currentUserId?: number;
}

/**
 * Column picker for the jobs export.
 *
 * The field list comes from the server so it always matches what the export
 * endpoint accepts. Selection order is not user-controlled — keys are emitted
 * in registry order so the file's column order stays predictable.
 */
export function ExportFieldsDialog({
  open,
  onOpenChange,
  fields,
  isLoading = false,
  isExporting = false,
  onConfirm,
  templates = [],
  onSaveTemplate,
  onDeleteTemplate,
  currentUserId,
}: ExportFieldsDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [templateName, setTemplateName] = useState('');
  const [shareTemplate, setShareTemplate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Seed the selection from the server's defaults once the fields arrive, and
  // re-seed each time the dialog is opened so a cancelled edit is discarded.
  useEffect(() => {
    if (open && fields.length > 0) {
      setSelected(new Set(fields.filter((f) => f.default).map((f) => f.key)));
      setTemplateName('');
      setShareTemplate(false);
    }
  }, [open, fields]);

  // Apply a saved template's columns. Unknown keys are ignored so a template
  // saved before a column was removed still loads instead of erroring.
  const applyTemplate = (templateId: string) => {
    const template = templates.find((t) => String(t.id) === templateId);
    if (!template) return;
    const valid = template.fields.filter((key) => fields.some((f) => f.key === key));
    setSelected(new Set(valid));
  };

  const toggle = (key: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(fields.map((f) => f.key)));
  const resetToDefault = () =>
    setSelected(new Set(fields.filter((f) => f.default).map((f) => f.key)));

  // Emit in registry order, not click order, so column order is deterministic.
  const orderedSelection = fields.filter((f) => selected.has(f.key)).map((f) => f.key);
  const noneSelected = orderedSelection.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Choose export columns</DialogTitle>
          <DialogDescription>
            Select which columns to include. Only rows matching your current filters are exported.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading columns…
          </div>
        ) : (
          <>
            {templates.length > 0 && (
              <div className="flex items-center gap-2">
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Load a saved template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={String(template.id)}>
                        {template.name}
                        {template.is_shared && template.user_id !== currentUserId
                          ? ` (shared by ${template.username ?? 'another user'})`
                          : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={selectAll}>
                Select all
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={resetToDefault}>
                Reset to default
              </Button>
              <span className="ml-auto text-xs text-muted-foreground">
                {orderedSelection.length} of {fields.length} selected
              </span>
            </div>

            <div className="max-h-[320px] overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2 py-2">
              {fields.map((field) => (
                <label
                  key={field.key}
                  className="flex items-center gap-2 text-sm cursor-pointer rounded-md px-2 py-1.5 hover:bg-accent"
                >
                  <Checkbox
                    checked={selected.has(field.key)}
                    onCheckedChange={(checked) => toggle(field.key, checked === true)}
                  />
                  <span>{field.label}</span>
                </label>
              ))}
            </div>

            {onSaveTemplate && (
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Save this selection as a template…"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={templateName.trim() === '' || noneSelected || isSaving}
                    onClick={async () => {
                      setIsSaving(true);
                      try {
                        await onSaveTemplate(templateName.trim(), orderedSelection, shareTemplate);
                        setTemplateName('');
                        setShareTemplate(false);
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>

                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <Checkbox
                    checked={shareTemplate}
                    onCheckedChange={(checked) => setShareTemplate(checked === true)}
                  />
                  <span>Share with my team (others can use it, only you can edit it)</span>
                </label>

                {onDeleteTemplate && templates.some((t) => t.user_id === currentUserId) && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {templates
                      .filter((t) => t.user_id === currentUserId)
                      .map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => onDeleteTemplate(template.id)}
                          className="inline-flex items-center gap-1 text-xs rounded-md border border-border px-2 py-1 hover:bg-accent"
                          title={`Delete "${template.name}"`}
                        >
                          <Trash2 className="w-3 h-3" />
                          {template.name}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="text-white"
            style={{ backgroundColor: '#E84A2B' }}
            disabled={noneSelected || isExporting || isLoading}
            onClick={() => onConfirm(orderedSelection)}
          >
            {isExporting ? 'Exporting…' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
